"use client";

import { Fragment } from "react";
import { Lightbulb } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

export interface SheetMetalFabricationTolerancesStandardsProps {
	/** 翻译命名空间，需包含 title、description、表头与各行列文案、footerNote、ctaButton */
	translationNamespace?: string;
}

/** 钣金公差与标准表：左列为类型键名，右列为描述键名 */
const TABLE_ROWS: readonly { labelKey: string; descKey: string }[] = [
	{ labelKey: "rowGeneralLabel", descKey: "rowGeneralDesc" },
	{ labelKey: "rowCuttingLabel", descKey: "rowCuttingDesc" },
	{ labelKey: "rowBendAngleLabel", descKey: "rowBendAngleDesc" },
	{ labelKey: "rowBendEdgeLabel", descKey: "rowBendEdgeDesc" },
	{ labelKey: "rowBendHoleLabel", descKey: "rowBendHoleDesc" },
];

/** 稿图：横线拉满视口（浅绿 #d6e3b5）；与表格、页脚共用 max-w-3xl 对齐 */
const fullBleedLine = "relative left-1/2 w-screen max-w-none -translate-x-1/2 border-[#d6e3b5]";

const contentMax = "w-full max-w-3xl";
const rowGridClass = `${contentMax} grid grid-cols-2 items-center gap-x-6 text-left sm:gap-x-8 md:grid-cols-[minmax(0,44%)_minmax(0,56%)] md:gap-x-10`;

const cellLeft = "min-w-0 text-xs font-bold leading-snug text-black sm:text-sm md:text-base";
const cellRight = "min-w-0 text-xs font-normal leading-snug text-black sm:text-sm md:text-base";
const cellHeaderRight = "min-w-0 text-xs font-bold leading-snug text-black sm:text-sm md:text-base";

/** 行内上下留白：文字在两条全宽横线之间垂直居中 */
const rowVerticalPad = "py-5 md:py-6";

export default function SheetMetalFabricationTolerancesStandards({
	translationNamespace = "sheetMetalFabricationTolerancesStandards",
}: SheetMetalFabricationTolerancesStandardsProps) {
	const t = useTranslations(translationNamespace);
	const { openModalInquiry } = useModalInquiryContext();

	return (
		<section className="overflow-x-hidden bg-[#f1fbd1] py-8 font-sans sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 w-full text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("title")}
				</h2>
				<p className="mb-6 w-full text-center text-sm font-normal leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t("description")}
				</p>

				{/* 横线全宽；两列内容在 max-w-3xl 内居中；行间浅绿线 */}
				<div>
					<div className={`${fullBleedLine} border-t`} aria-hidden />

					<div className={`flex justify-center px-4 sm:px-6 ${rowVerticalPad}`}>
						<div className={rowGridClass}>
							<div className={cellLeft}>{t("colToleranceType")}</div>
							<div className={cellHeaderRight}>{t("colDescription")}</div>
						</div>
					</div>

					<div className={`${fullBleedLine} border-b`} aria-hidden />

					{TABLE_ROWS.map((row) => (
						<Fragment key={row.labelKey}>
							<div className={`flex justify-center px-4 sm:px-6 ${rowVerticalPad}`}>
								<div className={rowGridClass}>
									<div className={cellLeft}>{t(row.labelKey)}</div>
									<div className={cellRight}>{t(row.descKey)}</div>
								</div>
							</div>
							<div className={`${fullBleedLine} border-b`} aria-hidden />
						</Fragment>
					))}
				</div>

				{/* 与表格同宽：左说明 + 右按钮 */}
				<div
					className={`flex pt-8 ${contentMax} container flex-col justify-center gap-6 sm:flex-row sm:items-center`}
				>
					<div className="flex justify-center">
						<div className="mt-2 flex min-w-0 flex-1 items-start gap-3 pr-16">
							<Lightbulb className="mt-0.5 h-6 w-6 shrink-0 text-[#62a02c]" weight="regular" aria-hidden />
							<p className="text-left text-sm leading-relaxed text-black md:text-base">{t("footerNote")}</p>
						</div>
						<button
							type="button"
							onClick={openModalInquiry}
							className="inline-flex w-full shrink-0 items-center justify-center rounded-md bg-[#62a02c] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#559026] sm:w-auto sm:px-8 sm:py-3 md:text-base"
						>
							{t("ctaButton")}
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}
